/* eslint-disable import/no-unresolved */
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Edit, UploadFile } from "@mui/icons-material";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
// eslint-disable-next-line import/no-unresolved
/* eslint-disable import/no-unresolved */
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { toast } from "react-hot-toast";

import { useProductTypeKeywordsMutations } from "../../../hooks/usePricing";
import { ProductTypeKeywords } from "../../../types/admin";

interface ProductTypeKeywordsTableProps {
  items: ProductTypeKeywords[];
  loading: boolean;
  onEdit: (item: ProductTypeKeywords) => void;
}

const COL = {
  type:     { minWidth: 160 },
  keywords: { minWidth: 400 },
  actions:  { minWidth: 80  },
} as const;

const thSx = (col: { minWidth: number }) => ({
  fontWeight: "bold",
  whiteSpace: "nowrap" as const,
  minWidth: col.minWidth,
});

const ProductTypeKeywordsTable: React.FC<ProductTypeKeywordsTableProps> = ({
  items,
  loading,
  onEdit,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const { uploadKeywords } = useProductTypeKeywordsMutations();

  const handleUploadClick = (id: number) => {
    setSelectedTypeId(id);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && selectedTypeId !== null) {
      try {
        await uploadKeywords.mutateAsync({ id: selectedTypeId, file });
        toast.success(t("admin.pricing.keywordsUploaded", "Keywords uploaded successfully"));
      } catch (error) {
        toast.error(t("admin.pricing.uploadFailed", "Failed to upload keywords"));
      }
    }
    // Reset
    if (fileInputRef.current) fileInputRef.current.value = "";
    setSelectedTypeId(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body1" color="text.secondary">
          {t("admin.pricing.noProductTypeKeywords", "No product type keywords found.")}
        </Typography>
      </Box>
    );
  }

  /** Comma-separated keyword chips (max 5 visible, rest as "+N more" chip) */
  const KeywordChips = ({ raw }: { raw: string }) => {
    const kws = raw
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);
    const visible = kws.slice(0, 6);
    const extra = kws.length - visible.length;
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {visible.map((kw) => (
          <Chip key={kw} label={kw} size="small" variant="outlined" />
        ))}
        {extra > 0 && (
          <Tooltip title={kws.slice(6).join(", ")}>
            <Chip label={`+${extra} more`} size="small" color="default" />
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <TableContainer
      component={Paper}
      sx={{ boxShadow: 2, overflowX: "auto", width: "100%" }}
    >
      <Table size="small" sx={{ minWidth: 640 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "grey.50" }}>
            <TableCell sx={thSx(COL.type)}>
              {t("admin.pricing.productType", "Product Type")}
            </TableCell>
            <TableCell sx={thSx(COL.keywords)}>
              {t("admin.pricing.keywords", "Keywords")}
            </TableCell>
            <TableCell sx={thSx(COL.actions)}>
              {t("common.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".csv"
            onChange={handleFileChange}
          />
          {items.map((item) => (
            <TableRow key={item.id} hover>
              <TableCell sx={{ minWidth: COL.type.minWidth, whiteSpace: "nowrap" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {item.type}
                </Typography>
              </TableCell>
              <TableCell sx={{ minWidth: COL.keywords.minWidth }}>
                <KeywordChips raw={item.keywords} />
              </TableCell>
              <TableCell sx={{ minWidth: COL.actions.minWidth, whiteSpace: "nowrap" }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title={t("admin.pricing.uploadKeywords", "Upload Keywords (CSV)")}>
                    <IconButton
                      size="small"
                      color="info"
                      onClick={() => handleUploadClick(item.id)}
                      disabled={uploadKeywords.isPending && selectedTypeId === item.id}
                    >
                      {uploadKeywords.isPending && selectedTypeId === item.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <UploadFile />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("common.edit")}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onEdit(item)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProductTypeKeywordsTable;
